import { ArrowRight, Sparkles } from 'lucide-react';
import styled from 'styled-components';
import { trackClick } from '../../services/adService';

const AdCard = styled.div`
  position: relative;
  background: ${props => props.variant === 'subtle' 
    ? `${props.theme.colors.muted}80` 
    : `linear-gradient(to bottom right, ${props.theme.colors.muted}, ${props.theme.colors.muted}80)`};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  padding: 1.25rem;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
  
  @media (min-width: 1024px) {
    padding: 1.5rem;
  }
  
  @media (min-width: 1440px) {
    padding: 1.75rem;
  }
`;

const DecorativeBlur1 = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 6rem;
  height: 6rem;
  background-color: ${props => props.theme.colors.category.teal};
  opacity: 0.1;
  border-radius: 9999px;
  filter: blur(32px);
  display: ${props => props.variant === 'subtle' ? 'none' : 'block'};
`;

const DecorativeBlur2 = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 5rem;
  height: 5rem;
  background-color: ${props => props.theme.colors.category.violet};
  opacity: 0.1;
  border-radius: 9999px;
  filter: blur(32px);
  display: ${props => props.variant === 'subtle' ? 'none' : 'block'};
`;

const Content = styled.div`
  position: relative;
`;

const SponsoredLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: ${props => props.variant === 'subtle' ? '0.5rem' : '0.75rem'};
`;

const SparklesIcon = styled(Sparkles)`
  width: 1rem;
  height: 1rem;
  color: ${props => props.theme.colors.category.amber};
  
  @media (min-width: 1024px) {
    width: 1.125rem;
    height: 1.125rem;
  }
  
  @media (min-width: 1440px) {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const SponsoredText = styled.span`
  font-size: 0.75rem;
  color: ${props => props.theme.colors.mutedForeground};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (min-width: 1024px) {
    font-size: 0.8125rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 0.875rem;
  }
`;

const AdTitle = styled.h3`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: ${props => props.variant === 'subtle' ? '1rem' : '1.125rem'};
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  margin-bottom: 0.5rem;
  
  @media (min-width: 1024px) {
    font-size: ${props => props.variant === 'subtle' ? '1.125rem' : '1.25rem'};
    margin-bottom: 0.625rem;
  }
  
  @media (min-width: 1440px) {
    font-size: ${props => props.variant === 'subtle' ? '1.25rem' : '1.375rem'};
    margin-bottom: 0.75rem;
  }
`;

const AdDescription = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  line-height: 1.75;
  margin-bottom: 1rem;
  
  @media (min-width: 1024px) {
    font-size: 0.9375rem;
    margin-bottom: 1.25rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  ${props => props.variant === 'subtle' ? `
    font-size: 0.875rem;
    font-weight: 500;
    color: ${props.theme.colors.foreground};
    text-decoration: none;
    transition: gap 0.3s;
    
    @media (min-width: 1024px) {
      font-size: 0.9375rem;
    }
    
    @media (min-width: 1440px) {
      font-size: 1rem;
    }
    
    &:hover {
      gap: 0.75rem;
    }
  ` : `
    padding: 0.5rem 1rem;
    background-color: ${props.theme.colors.foreground};
    color: ${props.theme.colors.background};
    border-radius: ${props.theme.borderRadius.xl};
    font-size: 0.875rem;
    font-weight: 500;
    text-decoration: none;
    transition: opacity 0.2s;
    
    @media (min-width: 1024px) {
      padding: 0.625rem 1.25rem;
      font-size: 0.9375rem;
    }
    
    @media (min-width: 1440px) {
      padding: 0.75rem 1.5rem;
      font-size: 1rem;
    }
    
    &:hover {
      opacity: 0.9;
    }
  `}
  
  svg {
    width: 1rem;
    height: 1rem;
    
    @media (min-width: 1024px) {
      width: 1.125rem;
      height: 1.125rem;
    }
    
    @media (min-width: 1440px) {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const NativeAd = ({ ad, onClick, variant = 'default' }) => {
  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick();
    } else {
      trackClick(ad._id);
      window.open(ad.clickUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const title = ad.title || '';
  const description = ad.description || '';
  const ctaText = ad.ctaText || 'Learn More';

  if (!title && !description) {
    return null; // Don't render empty ads
  }

  return (
    <AdCard 
      variant={variant}
      data-ad-id={ad._id}
      onClick={handleClick}
    >
      <DecorativeBlur1 variant={variant} />
      <DecorativeBlur2 variant={variant} />
      <Content>
        <SponsoredLabel variant={variant}>
          <SparklesIcon />
          <SponsoredText>Sponsored</SponsoredText>
        </SponsoredLabel>
        
        {title && <AdTitle variant={variant}>{title}</AdTitle>}
        {description && <AdDescription>{description}</AdDescription>}
        
        <CTAButton 
          href={ad.clickUrl} 
          variant={variant}
          onClick={handleClick}
          target="_blank"
          rel="noopener noreferrer"
        >
          {ctaText}
          <ArrowRight />
        </CTAButton>
      </Content>
    </AdCard>
  );
};

export default NativeAd;



